import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Toast } from 'primereact/toast';
import '../../css/Pages.css';
import InitiativeListing from '../../components/Initiative/InitiativeListing';
import InitiativeForm from '../../components/Initiative/InitiativeForm';
import { useConfirmDeleteDialog } from '../../hooks/useConfirmDeleteDialog';

const Initiative = () => {
    const toast = useRef(null);
    const { confirmDeleteDialog } = useConfirmDeleteDialog();

    const initialForm = {
        Name: '', InitiativeLevel: '', SolutionType: '', HostInstitution: '',
        ApprovalDate: '', CertificateNumber: '', Summary: '', MembersJSON: '[]', AttachmentUrl: ''
    };

    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'Name', direction: 'asc' });
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState(initialForm);
    const [members, setMembers] = useState([]);
    const [editId, setEditId] = useState(null);

    const [users, setUsers] = useState([]);
    const [levels, setLevels] = useState([]);
    const [solutionTypes, setSolutionTypes] = useState([]);

    const getAuthHeaders = useCallback(() => {
        return { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` };
    }, []);

    const handleLogout = useCallback(() => {
        toast.current?.show({ severity: 'error', summary: 'Lỗi xác thực', detail: 'Phiên đăng nhập hết hạn hoặc không có quyền truy cập!', life: 3000 });
        setTimeout(() => {
            localStorage.removeItem('user');
            localStorage.removeItem('accessToken');
            window.location.href = '/login';
        }, 1500);
    }, []);

    const fetchDropdowns = useCallback(async () => {
        try {
            const baseUrl = process.env.REACT_APP_API_BASE_URL;
            const headers = getAuthHeaders();

            const [uRes, lRes, stRes] = await Promise.all([
                fetch(`${baseUrl}/user?mode=selection`, { headers }),
                fetch(`${baseUrl}/initiativelevel`, { headers }),
                fetch(`${baseUrl}/solutiontype`, { headers })
            ]);

            if (uRes.status === 401 || uRes.status === 403 || lRes.status === 401 || lRes.status === 403 || stRes.status === 401 || stRes.status === 403) {
                handleLogout();
                return;
            }

            if (uRes.ok) setUsers(await uRes.json());
            if (lRes.ok) setLevels(await lRes.json());
            if (stRes.ok) setSolutionTypes(await stRes.json());
        } catch (e) {
            console.error(e);
        }
    }, [getAuthHeaders, handleLogout]);

    const fetchData = useCallback(async (isBackground = false) => {
        if (!isBackground) setIsLoading(true);
        try {
            const currentUser = JSON.parse(localStorage.getItem('user')) || {};
            if (!currentUser || !currentUser.Id) {
                handleLogout();
                if (!isBackground) setIsLoading(false);
                return;
            }

            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/initiative?userId=${currentUser.Id}`, {
                headers: getAuthHeaders()
            });

            if (res.status === 401 || res.status === 403) {
                handleLogout();
                return;
            }

            if (res.ok) {
                const result = await res.json();
                setData(result);
            }
        } catch (e) {
            console.error(e);
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Không thể kết nối đến máy chủ!', life: 3000 });
        } finally {
            if (!isBackground) setIsLoading(false);
        }
    }, [getAuthHeaders, handleLogout]);

    useEffect(() => {
        fetchData();
        fetchDropdowns();
    }, [fetchData, fetchDropdowns]);

    useEffect(() => {
        let result = [...data];

        if (searchQuery) {
            result = result.filter(item => item.Name?.toLowerCase().includes(searchQuery));
        }

        if (sortConfig !== null) {
            result.sort((a, b) => {
                if (sortConfig.key === 'ApprovalDate') {
                    const dateA = a.ApprovalDate ? new Date(a.ApprovalDate).getTime() : 0;
                    const dateB = b.ApprovalDate ? new Date(b.ApprovalDate).getTime() : 0;
                    if (dateA < dateB) return sortConfig.direction === 'asc' ? -1 : 1;
                    if (dateA > dateB) return sortConfig.direction === 'asc' ? 1 : -1;
                    return 0;
                } else {
                    const valA = (a[sortConfig.key] || '').toString().trim();
                    const valB = (b[sortConfig.key] || '').toString().trim();
                    const comparison = valA.localeCompare(valB, 'vi', { sensitivity: 'base' });
                    return sortConfig.direction === 'asc' ? comparison : -comparison;
                }
            });
        }

        setFilteredData(result);
    }, [data, searchQuery, sortConfig]);

    const handleSearch = (e) => {
        setSearchQuery(e.target.value.toLowerCase());
    };

    const handleStatusChange = async (id, status) => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/initiative?id=${id}&status=${encodeURIComponent(status)}`, {
                method: 'PATCH',
                headers: getAuthHeaders()
            });

            if (res.status === 401 || res.status === 403) {
                handleLogout();
                return;
            }

            if (res.ok) {
                toast.current.show({ severity: 'success', summary: 'Thành công', detail: 'Đã cập nhật trạng thái!' });
                fetchData(true);
            } else {
                let errData;
                try { errData = await res.json(); } catch (e) { errData = { message: "Thao tác thất bại!" }; }
                toast.current.show({ severity: 'error', summary: 'Lỗi', detail: errData.message });
            }
        } catch (e) {
            toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Thao tác thất bại do lỗi kết nối!' });
        }
    };

    const handleBulkStatusChange = async (ids, newStatus) => {
        if (newStatus === 'Đã gửi duyệt') {
            const invalidItems = data.filter(d => ids.includes(d.Id) && (d.Status === 'Đã gửi duyệt' || d.Status === 'Đã duyệt'));
            if (invalidItems.length > 0) {
                return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng bỏ chọn các hồ sơ đã gửi hoặc đã duyệt để có thể thao tác!', life: 4000 });
            }
        } else if (newStatus === 'Lưu nháp') {
            const invalidItems = data.filter(d => ids.includes(d.Id) && d.Status !== 'Đã gửi duyệt');
            if (invalidItems.length > 0) {
                return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Chỉ có thể hủy đối với hồ sơ có trạng thái "Đã gửi duyệt"!', life: 4000 });
            }
        }

        setIsLoading(true);
        try {
            const promises = ids.map(id =>
                fetch(`${process.env.REACT_APP_API_BASE_URL}/initiative?id=${id}&status=${encodeURIComponent(newStatus)}`, {
                    method: 'PATCH',
                    headers: getAuthHeaders()
                })
            );

            const results = await Promise.all(promises);
            const allOk = results.every(res => res.ok);

            if (allOk) {
                toast.current.show({
                    severity: 'success', summary: 'Thành công',
                    detail: newStatus === 'Đã gửi duyệt' ? `Đã gửi yêu cầu duyệt ${ids.length} mục!` : `Đã hủy gửi duyệt ${ids.length} mục!`, life: 3000
                });
            } else {
                toast.current.show({ severity: 'error', summary: 'Lỗi', detail: "Một số thao tác thất bại!", life: 3000 });
            }
            fetchData(true);
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = async (item) => {
        let parsedMembers = [];
        try {
            parsedMembers = JSON.parse(item.MembersJSON || '[]');
        } catch (e) {
            parsedMembers = [];
        }

        const missingIds = parsedMembers
            .map(m => m.userId || m.UserId || m.Id || m.id)
            .filter(id => id && !users.find(u => u.Id.toString() === id.toString()));

        if (missingIds.length > 0) {
            try {
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user?includeIds=${missingIds.join(',')}`, {
                    headers: getAuthHeaders()
                });

                if (res.status === 401 || res.status === 403) {
                    handleLogout();
                    return;
                }

                if (res.ok) {
                    const extraUsers = await res.json();
                    setUsers(prevUsers => {
                        const merged = [...prevUsers];
                        extraUsers.forEach(eu => {
                            if (!merged.find(u => u.Id === eu.Id)) {
                                merged.push(eu);
                            }
                        });
                        return merged;
                    });
                }
            } catch (error) {
                console.error("Lỗi tải thông tin thành viên bổ sung:", error);
            }
        }

        setEditId(item.Id);
        setFormData(item);
        setMembers(parsedMembers);
        setIsModalOpen(true);
    };

    const handleDelete = (id) => {
        const item = data.find(d => d.Id === id);
        if (item?.Status === 'Đã gửi duyệt' || item?.Status === 'Đã duyệt') {
            toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Hồ sơ đã khóa, không thể xóa!', life: 3000 });
            return;
        }

        confirmDeleteDialog({
            header: 'Xác nhận xóa', message: 'Bạn có chắc chắn xóa sáng kiến này?',
            accept: async () => {
                try {
                    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/initiative?id=${id}`, {
                        method: 'DELETE',
                        headers: getAuthHeaders()
                    });

                    if (res.status === 401 || res.status === 403) {
                        handleLogout();
                        return;
                    }

                    if (res.ok) {
                        toast.current.show({ severity: 'success', summary: 'Thành công', detail: 'Đã xóa bản ghi!' });
                        fetchData(true);
                    } else {
                        let errData;
                        try { errData = await res.json(); } catch (e) { errData = { message: "Xóa thất bại!" }; }
                        toast.current.show({ severity: 'error', summary: 'Lỗi', detail: errData.message });
                    }
                } catch (e) {
                    toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!' });
                }
            }
        });
    };

    const handleBulkDelete = (ids) => {
        const invalidItems = data.filter(d => ids.includes(d.Id) && (d.Status === 'Đã gửi duyệt' || d.Status === 'Đã duyệt'));
        if (invalidItems.length > 0) {
            return toast.current.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng bỏ chọn các hồ sơ đã gửi hoặc đã duyệt khỏi danh sách cần xóa!', life: 4000 });
        }

        confirmDeleteDialog({
            header: 'Xác nhận xóa hàng loạt',
            message: `Bạn có chắc chắn muốn xóa ${ids.length} sáng kiến đã chọn?`,
            accept: async () => {
                setIsLoading(true);
                try {
                    const promises = ids.map(id =>
                        fetch(`${process.env.REACT_APP_API_BASE_URL}/initiative?id=${id}`, {
                            method: 'DELETE',
                            headers: getAuthHeaders()
                        })
                    );

                    const results = await Promise.all(promises);
                    const allOk = results.every(res => res.ok);

                    if (allOk) {
                        toast.current.show({ severity: 'success', summary: 'Thành công', detail: `Đã xóa ${ids.length} bản ghi!` });
                    } else {
                        toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Một số bản ghi xóa thất bại!' });
                    }
                    fetchData(true);
                } catch (error) {
                    toast.current.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối!' });
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    const handleAddNew = () => {
        const currentUser = JSON.parse(localStorage.getItem('user')) || {};
        setMembers([{
            userId: currentUser.Id || '',
            fullName: `${currentUser.LastName || ''} ${currentUser.FirstName || ''}`.trim(),
            isMainAuthor: true
        }]);
        setEditId(null);
        setFormData(initialForm);
        setIsModalOpen(true);
    };

    return (
        <div className="page-container">
            <Toast ref={toast} position="top-right" baseZIndex={99999} style={{ zIndex: 99999 }} />

            <div className="page-header">
                <div className="header-title"><h2>QUẢN LÝ SÁNG KIẾN</h2></div>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
                <button className="btn-add-new" onClick={handleAddNew} style={{ margin: 0 }}>
                    <i className="fa-solid fa-plus"></i> Thêm mới
                </button>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px', marginBottom: '20px' }}>
                <p className="sub-title" style={{ margin: 0 }}>DANH SÁCH SÁNG KIẾN</p>
                <div className="search-wrapper" style={{ position: 'relative', width: '100%', maxWidth: '350px' }}>
                    <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', left: '10px', top: '12px', color: '#888' }}></i>
                    <input
                        type="text"
                        placeholder="Tìm kiếm"
                        className="form-input"
                        style={{ width: '100%', paddingLeft: '35px' }}
                        onChange={handleSearch}
                        value={searchQuery}
                    />
                </div>
            </div>

            <InitiativeListing
                data={filteredData}
                searchQuery={searchQuery}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onSendSubmit={(id) => handleStatusChange(id, 'Đã gửi duyệt')}
                onCancelSubmit={(id) => handleStatusChange(id, 'Lưu nháp')}
                onBulkSend={(ids) => handleBulkStatusChange(ids, 'Đã gửi duyệt')}
                onBulkCancel={(ids) => handleBulkStatusChange(ids, 'Lưu nháp')}
                onBulkDelete={handleBulkDelete}
                isLoading={isLoading}
                toast={toast}
                sortConfig={sortConfig}
                requestSort={(key) => {
                    let direction = 'asc';
                    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
                    setSortConfig({ key, direction });
                }}
            />

            <InitiativeForm
                isOpen={isModalOpen}
                onClose={() => { setIsModalOpen(false); setFormData(initialForm); setMembers([]); setEditId(null); }}
                formData={formData}
                setFormData={setFormData}
                isEditing={!!editId}
                users={users}
                levels={levels}
                solutionTypes={solutionTypes}
                members={members}
                setMembers={setMembers}
                toast={toast}
                fetchData={fetchData}
            />
        </div>
    );
};

export default Initiative;